import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import i18next from "i18next";
import { useRef } from "react";

export const COMPILE_MARKETS = ["oneXTwo", "goalLines", "asianHandicap", "btts"];

export function flattenCompilePreview(preview = []) {
  const teams = new Map();
  for (const fixture of preview) {
    for (const team of fixture?.teams || []) {
      const key = `${fixture.season_id}:${team.team_id}`;
      if (!teams.has(key)) teams.set(key, { ...team, season_id: fixture.season_id });
    }
  }
  return [...teams.values()].sort(
    (a, b) => Number(a.season_id) - Number(b.season_id) ||
      Number(a.rank || Number.MAX_SAFE_INTEGER) - Number(b.rank || Number.MAX_SAFE_INTEGER)
  );
}

export function mergeCompiledFixtureDetails(rows, details) {
  const byId = new Map(details.map((detail) => [Number(detail.id), detail]));
  return rows.map((row) => {
    const detail = byId.get(Number(row.id));
    return detail ? { ...row, ...detail, ...(detail.probs || {}) } : row;
  });
}

export function draftFixtureDetail(compileResponse, persisted = false) {
  const detail = compileResponse?.detail;
  if (!detail || typeof detail !== "object") return null;
  const localDetail = { ...detail, ...(detail.probs || {}) };
  if (!persisted) delete localDetail.status;
  return localDetail;
}

export function restoreFixtureDraftRows(rows, fixtureId, baselineRow) {
  return rows.map((fixture) => Number(fixture.id) === Number(fixtureId)
    ? { ...fixture, ...baselineRow }
    : fixture
  );
}

export function buildFixtureDetailRefreshRequests(fixtures) {
  const bySeason = new Map();
  for (const fixture of fixtures) {
    const seasonId = Number(fixture?.season_id);
    const key = Number.isFinite(seasonId) ? String(seasonId) : "unknown";
    if (!bySeason.has(key)) bySeason.set(key, []);
    bySeason.get(key).push(fixture);
  }
  return [...bySeason].map(([seasonKey, seasonFixtures]) => ({
    fixtures: seasonFixtures,
    ...(seasonKey === "unknown" ? {} : { season_id: Number(seasonKey) }),
  }));
}

export function rankTeamSettings(rows = []) {
  return [...rows]
    .map((team) => ({
      ...team,
      total_goals: Number.isFinite(Number(team?.total_goals))
        ? Number(team.total_goals)
        : Number(team?.for_total) + Number(team?.against_total),
    }))
    .filter((team) => Number.isFinite(Number(team?.total_strength)))
    .sort((a, b) => Number(b.total_strength) - Number(a.total_strength))
    .map((team, index) => ({ ...team, rank: index + 1 }));
}

export function CompilePreviewDialog({
  open,
  fixtureCount,
  multiCompile = false,
  calculationMethod = "standard",
  preview,
  teams: suppliedTeams,
  loading,
  running,
  error,
  success,
  missingSeasons = [],
  onCancel,
  onReviewSettings,
  onCalculationMethodChange,
  onConfirm,
}) {
  const confirmationPending = useRef(false);
  const teams = Array.isArray(suppliedTeams) && suppliedTeams.length
    ? rankTeamSettings(suppliedTeams)
    : flattenCompilePreview(preview);
  const blocked = loading || running || Boolean(error) || Boolean(success) || missingSeasons.length > 0;

  async function handleConfirm() {
    if (blocked || confirmationPending.current) return;
    confirmationPending.current = true;
    try {
      await onConfirm();
    } finally {
      confirmationPending.current = false;
    }
  }

  return (
    <Dialog open={open} onClose={running ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{i18next.t("help.compilePreview.title", { count: fixtureCount })}</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
        <Typography>{i18next.t("help.compilePreview.description")}</Typography>

        {(loading || running) && (
          <Box role="status" aria-live="polite">
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 800 }}>
              {i18next.t(
                running ? "help.compilePreview.running" : "help.compilePreview.loading",
                { count: fixtureCount }
              )}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {missingSeasons.length > 0 && (
          <Alert severity="warning">
            {i18next.t("help.compilePreview.missingSettings", {
              seasons: missingSeasons.map((item) => item.season_id).join(", "),
            })}
          </Alert>
        )}

        {!loading && (
          <>
            {!multiCompile && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {i18next.t("help.compilePreview.settingsTitle")}
                </Typography>
                <Alert icon={<InfoOutlinedIcon />} severity="info" sx={{ mt: 1 }}>
                  {i18next.t("help.compilePreview.settingsInfo")}
                </Alert>
                <Paper variant="outlined" sx={{ mt: 1, maxHeight: 280, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{i18next.t("help.compilePreview.rank")}</TableCell>
                        <TableCell>{i18next.t("help.compilePreview.team")}</TableCell>
                        <TableCell align="right">{i18next.t("help.compilePreview.strength")}</TableCell>
                        <TableCell align="right">{i18next.t("help.compilePreview.expectedGoalAverage")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teams.map((team) => (
                        <TableRow key={`${team.season_id}:${team.team_id}`}>
                          <TableCell>{team.rank || "–"}</TableCell>
                          <TableCell>{team.team_name || `#${team.team_id}`}</TableCell>
                          <TableCell align="right">
                            {Number.isFinite(Number(team.total_strength))
                              ? Number(team.total_strength).toFixed(3)
                              : "–"}
                          </TableCell>
                          <TableCell align="right">
                            {Number.isFinite(Number(team.total_goals))
                              ? Number(team.total_goals).toFixed(3)
                              : "–"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            )}

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {i18next.t("help.compilePreview.methodTitle")}
              </Typography>
              <RadioGroup
                value={calculationMethod}
                onChange={(event) => onCalculationMethodChange?.(event.target.value)}
              >
                <FormControlLabel
                  value="standard"
                  control={<Radio />}
                  disabled={running}
                  label={i18next.t("help.compilePreview.standardMethod")}
                />
                <FormControlLabel
                  value="direct"
                  control={<Radio />}
                  disabled={running}
                  label={i18next.t("help.compilePreview.directSimulation")}
                />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" display="block">
                {i18next.t("help.compilePreview.methodInfo")}
              </Typography>
            </Paper>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {i18next.t("help.compilePreview.marketsTitle")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {i18next.t("help.compilePreview.marketsInfo")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                {COMPILE_MARKETS.map((market) => (
                  <Typography key={market} variant="body2" sx={{ fontWeight: 700 }}>
                    ✓ {i18next.t(`help.compilePreview.markets.${market}`)}
                  </Typography>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!multiCompile && (
          <Button onClick={onReviewSettings} disabled={running || !onReviewSettings}>
            {i18next.t("help.compilePreview.leaveForSettings")}
          </Button>
        )}
        <Button onClick={onCancel} disabled={running}>
          {i18next.t("help.compilePreview.cancel")}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={blocked}>
          {i18next.t("help.compilePreview.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
